import { Component, computed, effect, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '@shared/components/base.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { ICreateCustomerDialogData, ICreateCustomerDialogForm } from './create-customer-dialog.interface';
import { CreateCustomerDialogStore } from './create-customer-dialog.store';

@Component({
  imports: [
    SideDialogComponent,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    DotsSpinnerComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatErrorComponent,
    FileUploaderComponent,
  ],
  providers: [CreateCustomerDialogStore],
  selector: 'app-create-customer-dialog',
  templateUrl: './create-customer-dialog.component.html',
})
export class CreateCustomerDialogComponent extends BaseComponent {
  public readonly form: FormGroup<ICreateCustomerDialogForm> = new FormGroup({
    iconId: new FormControl<string | null>(null),
    imageFile: new FormControl<ICustomFile | null>(null, imageExtensionValidator()),
    name: new FormControl<string | null>(null, Validators.required),
  });

  public readonly data = inject<ICreateCustomerDialogData>(MAT_DIALOG_DATA);
  public readonly store = inject(CreateCustomerDialogStore);
  private readonly dialogRef = inject(MatDialogRef<CreateCustomerDialogComponent>);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly eventTrackingService = inject(EventTrackingService);

  public readonly title = computed(() => {
    return this.translate.instant(
      this.data.customer ? 'dialogs.createCustomerDialog.updateTitle' : 'dialogs.createCustomerDialog.createTitle',
    );
  });
  public readonly submitButtonText = computed(() => {
    return this.translate.instant(
      this.data.customer ? 'shared.form.save' : 'dialogs.createCustomerDialog.createCustomer',
    );
  });

  constructor() {
    super();

    this.fillFormWithCustomer();
    this.createNameErrorEffect();
    this.clearNameErrorOnEdit();

    effect(() => {
      const customer = this.store.customer$();
      const customerSubmitLoading = this.store.customerSubmitLoading$();

      if (!customer || customerSubmitLoading) {
        return;
      }

      if (!this.data.customer) {
        if (this.data.redirectToCustomerDetails) {
          this.eventTrackingService.track(EEventType.customerCreated, {
            customerId: customer.id,
            source: 'customers_list_dialog',
          });
        } else {
          this.eventTrackingService.track(EEventType.campaignCreationClientCreated, {
            customerId: customer.id,
          });
        }
      }

      if (this.data.redirectToCustomerDetails) {
        this.router.navigate(['/customers', customer.id]);
        this.dialogRef.close({ iconId: customer.iconId, id: customer.id, name: customer.name });
      } else {
        this.dialogRef.close({ iconId: customer.iconId, id: customer.id, name: customer.name });
      }
    });
  }

  public onSubmit(): void {
    if (this.form.invalid || this.store.customerSubmitLoading$()) {
      return;
    }

    const { iconId, name } = this.form.getRawValue();

    if (this.data.customer) {
      this.store.updateCustomer({
        customer: {
          iconId: iconId!,
          name: name!,
        },
        id: this.data.customer.id,
      });
    } else {
      this.store.createCustomer({ iconId: iconId!, name: name! });
    }
  }

  public onImageSelected(event: { file: File; id: string }): void {
    const imageFileControl = this.form.get('imageFile');
    const iconControl = this.form.get('iconId');

    imageFileControl?.setValue(event.file);
    iconControl?.setValue(event.id);
  }

  public onImageRemoved(): void {
    const imageFileControl = this.form.get('imageFile');
    const iconControl = this.form.get('iconId');

    imageFileControl?.setValue(null);
    iconControl?.setValue(null);
  }

  public onCloseClick(): void {
    this.dialogRef.close();
  }

  private createNameErrorEffect(): void {
    effect(() => {
      const nameError = this.store.nameError$();
      const nameControl = this.form.get('name');

      if (nameError) {
        nameControl?.markAsTouched();
        nameControl?.setErrors({ error: true });
      } else if (nameControl?.hasError('error')) {
        nameControl.setErrors(null);
      }
    });
  }

  private clearNameErrorOnEdit(): void {
    this.form.get('name')?.valueChanges.subscribe(() => {
      if (this.store.nameError$()) {
        this.store.clearNameError();
      }
    });
  }

  private fillFormWithCustomer(): void {
    if (!this.data.customer) {
      return;
    }

    const { iconId, name } = this.data.customer;

    this.form.patchValue({
      iconId,
      imageFile: iconId ? FileUploaderService.createCustomFile(iconId) : null,
      name,
    });
  }
}
